import type { ObjectForm } from "@pdfking/forms"
import { ComputeScale } from "@pdfking/imposition-form"
import { DEFAULT_FILE_BLEED } from "@pdfking/imposition/constants"
import type { SinglePagePdf } from "@pdfking/pdfengine"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import { NullQ } from "@pdfking/structs"
import { DeleteDuplicates, Items, Map, RecordCreate } from "@pdfking/tfunc"

import { useTranslationTyped } from "../../i18n/useTranslation"
import { IDMixin } from "../../mixins/IDMixin"
import { dom_id } from "../dispatch/DomId"
import { DjInput } from "../fields/DjInput"
import { FieldMixin } from "../mixins/FieldMixin"
import { AtomFormMixin } from "../mixins/FormMixin"
import { NodeMixin } from "../mixins/NodeMixin"
import { sibling_file_id } from "./SiblingFileId"

const MODES: [string, Record<string, string>][] = [
  ["CUT_AREA", { contain: "CONTAIN", fill: "FILL", sketch: "SKETCH" }],
  [
    "BLEED_AREA",
    {
      "contain-bleed": "CONTAIN",
      "fill-bleed": "FILL",
      "sketch-bleed": "SKETCH",
    },
  ],
]

export const FileScale = CreateRenderFunction({
  mixins: [NodeMixin, FieldMixin, IDMixin, AtomFormMixin] as const,
  computed: {
    media(): SinglePagePdf | undefined {
      const name = sibling_file_id(this.parent as ObjectForm<Record<string, unknown>> | undefined)
      const file_media = this.ctx.file_media
      return (name && this.ctx.sample_media && file_media[name]) || undefined
    },
  },
  render() {
    const filecheck = useTranslationTyped("filecheck")
    const media = this.media
    const outer = this.ctx.sample_media
    // each mode resolved to its computed scale value; the option label is the
    // "Group: Mode" pair, the value is the scale the input commits as-is
    const options =
      media && outer
        ? DeleteDuplicates(
            MODES.flatMap(([group, opts]) =>
              Items(opts).map(([mode, label]) => ({
                label: `${filecheck[group as keyof typeof filecheck]()}: ${filecheck[label as keyof typeof filecheck]()}`,
                value: this.node!.format(
                  ComputeScale(
                    {
                      bleed: DEFAULT_FILE_BLEED,
                      inner: media.trim,
                      outer,
                    },
                    mode
                  )
                ),
              }))
            ),
            o => o.value
          )
        : []
    const suggestions = RecordCreate(Map(({ label, value }) => [value, label] as const, options))
    return (
      <DjInput
        disabled={this.disabled}
        error={this.node!.error}
        dom_id={dom_id(this)}
        input={String(this.node!.input ?? "")}
        oncommit={v => this.node!.validate(v)}
        placeholder={!NullQ(this.node!.value) ? this.node!.format(this.node!.value) : undefined}
        suggestions={suggestions}
      />
    )
  },
})
